import { UsersWithNewAccessReportEntry } from 'common/services/api/Reports/UsersWithNewAccess.api';
import dayjs from 'dayjs';

const USERS_WITH_NEW_ACCESS_REPORT_BASE_FILENAME = 'NewUsersAccessReport';

/**
 * Generates a dynamic filename for the CSV report
 * @param appSuiteName - The name of the selected app suite
 * @param tenantName - The name of the selected tenant
 * @returns Formatted filename with app suite, tenant, and timestamp
 */
export const generateReportFilename = (appSuiteName: string, tenantName: string): string => {
    // Create a short timestamp (YYYYMMDD_HHMM format)
    const now = dayjs();
    const shortTimestamp = now.format('YYYYMMDD_HHmm');

    // Clean the names to be filename-safe (remove special characters)
    const cleanAppSuite = appSuiteName.replaceAll(/[^a-zA-Z0-9]/g, '');
    const cleanTenant = tenantName.replaceAll(/[^a-zA-Z0-9]/g, '');

    return `${USERS_WITH_NEW_ACCESS_REPORT_BASE_FILENAME}_${cleanAppSuite}_${cleanTenant}_${shortTimestamp}`;
};

// Required CSV columns for the Users with New Access report
export const REQUIRED_CSV_COLUMNS = [
    'userId',
    'firstName',
    'lastName',
    'email',
    'companyKey',
    'licenseBillingRecord',
    'userTenantMembershipStartDate',
    'aclResourceDisplayName',
    'userTenantMembershipEndDate'
] as const;

/**
 * Normalizes the report data to ensure all required columns are present
 * @param data - The raw report data from the backend
 * @returns Normalized data with all required columns
 */
export const normalizeReportData = (data: UsersWithNewAccessReportEntry[]): Record<string, string>[] => {
    // If no data is returned, create a single empty row with all required columns
    // This ensures the CSV header is still generated with all required columns
    if (!data || data.length === 0) {
        const emptyEntry: Record<string, string> = {};
        for (const column of REQUIRED_CSV_COLUMNS) {
            emptyEntry[column] = '';
        }
        return [emptyEntry];
    }

    return data.map(entry => {
        const normalizedEntry: Record<string, string> = {};

        // Ensure all required columns are present with default empty values
        for (const column of REQUIRED_CSV_COLUMNS) {
            normalizedEntry[column] = entry[column] ?? '';
        }

        return normalizedEntry;
    });
};
